import { isTelegramWebApp, tg } from '@/lib/platform';

/**
 * Feature-detected wrappers around the Telegram WebApp SDK. Every wrapper
 * falls back to a browser equivalent (or a no-op) when the app is running
 * as a PWA, so calling code never has to branch on modality.
 */

export const getInitData = (): string => {
  if (!isTelegramWebApp()) {
    return '';
  }
  return tg()?.initData ?? '';
};

/**
 * Tell the Telegram host we're ready and ask for the "full size" view
 * — Telegram chrome + the device status bar stay visible, but the
 * webview is the maximum height the host will give us. We deliberately
 * stop short of WebApp.requestFullscreen() (true device fullscreen):
 * users still need Telegram's top bar visible for orientation, and the
 * device status bar is useful in-flow.
 *
 *   ready()   — unblocks Telegram's loading splash
 *   expand()  — full-size webview; also kills the swipe-down-to-close
 *               gesture on iOS so users can scroll modal content
 *
 * No-op in a regular browser.
 */
export const ready = (): void => {
  if (!isTelegramWebApp()) {
    return;
  }
  const webApp = tg();
  webApp?.ready?.();
  webApp?.expand?.();
};

/**
 * CloudStorage — Telegram's per-user persistent kv store, replicated across
 * a user's Telegram clients. Falls back to localStorage when the SDK is not
 * present so dev / PWA still has a place to put the access token.
 */

interface CloudStorageGlobal {
  getItem?: (key: string, callback: (err: Error | null, value: string | null) => void) => void;
  setItem?: (key: string, value: string, callback?: (err: Error | null) => void) => void;
  removeItem?: (key: string, callback?: (err: Error | null) => void) => void;
}

const cloudStorage = (): CloudStorageGlobal | undefined => {
  if (!isTelegramWebApp()) {
    return undefined;
  }
  return (tg() as { CloudStorage?: CloudStorageGlobal } | undefined)?.CloudStorage;
};

export const cloudGet = (key: string): Promise<string | null> =>
  new Promise((resolve) => {
    const cs = cloudStorage();
    if (!cs?.getItem) {
      resolve(localStorage.getItem(key));
      return;
    }
    cs.getItem(key, (err, value) => {
      if (err) {
        resolve(localStorage.getItem(key));
        return;
      }
      resolve(value ?? null);
    });
  });

export const cloudSet = (key: string, value: string): Promise<void> =>
  new Promise((resolve) => {
    const cs = cloudStorage();
    if (!cs?.setItem) {
      localStorage.setItem(key, value);
      resolve();
      return;
    }
    cs.setItem(key, value, () => resolve());
  });

export const cloudRemove = (key: string): Promise<void> =>
  new Promise((resolve) => {
    const cs = cloudStorage();
    if (!cs?.removeItem) {
      localStorage.removeItem(key);
      resolve();
      return;
    }
    cs.removeItem(key, () => resolve());
  });

/**
 * Open a t.me link inside Telegram (uses the WebApp's openTelegramLink so
 * the host stays inside the app instead of bouncing to system browser).
 * In PWA, falls back to window.location.assign.
 */
export const openTelegramLink = (url: string): void => {
  const webApp = tg() as { openTelegramLink?: (url: string) => void } | undefined;
  if (isTelegramWebApp() && webApp?.openTelegramLink) {
    webApp.openTelegramLink(url);
    return;
  }
  if (typeof window !== 'undefined') {
    window.location.assign(url);
  }
};

/**
 * Close the Mini-App (returns the user to chat). PWA no-op since there's
 * no host to close to.
 */
export const close = (): void => {
  const webApp = tg() as { close?: () => void } | undefined;
  webApp?.close?.();
};

/**
 * Whether the current Telegram client supports requestContact (Bot API
 * 6.9+, iOS/Android Telegram 9.7+). Used to gate the in-app one-tap
 * phone share — older clients fall back to the bot DM deep-link.
 */
export const canRequestContact = (): boolean => {
  if (!isTelegramWebApp()) return false;
  return typeof tg()?.requestContact === 'function';
};

/**
 * Show Telegram's native "Share your phone?" popup. Resolves with the
 * user's choice; the contact data itself is delivered to the bot's
 * webhook (not back here), so callers should refetch the user profile
 * after a `true` resolution to pick up the persisted phone number.
 */
export const requestTelegramContact = (): Promise<boolean> =>
  new Promise((resolve) => {
    const webApp = tg();
    if (!webApp?.requestContact) {
      resolve(false);
      return;
    }
    try {
      webApp.requestContact((shared: boolean) => resolve(!!shared));
    } catch {
      resolve(false);
    }
  });
